import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Distances from the two viewport edges the card is anchored to. */
type Position = {
  blockOffset: number;
  inlineOffset: number;
};

type Point = {
  x: number;
  y: number;
};

type Anchor = {
  block: 'bottom' | 'top';
  inline: 'left' | 'right';
};

/** A corner in logical terms: `start` is the left in LTR and the right in RTL. */
type InitialPosition = 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';

interface DraggableProps {
  /** Content to render inside the draggable card. */
  children: ReactNode;

  /** Additional class names for the card container. */
  className?: string;

  /** Initial position of the card on the screen. */
  initialPosition?: InitialPosition;
}

/** Margin from the edge of the viewport. */
const EDGE_MARGIN = 20;

/** Threshold in pixels to distinguish a click from a drag. */
const DRAG_THRESHOLD = 5;

/** Debounce delay in milliseconds for re-clamping the card after a resize. */
const RESIZE_DEBOUNCE_MS = 100;

/** Duration in milliseconds of the glide back to the starting corner. */
const RETURN_DURATION_MS = 200;

function toAnchor(initialPosition: InitialPosition, isRtl: boolean): Anchor {
  const [block, inline] = initialPosition.split('-') as [Anchor['block'], 'end' | 'start'];
  if (isRtl) {
    return { block, inline: inline === 'start' ? 'right' : 'left' };
  }
  return { block, inline: inline === 'start' ? 'left' : 'right' };
}

/** Picks the pair of viewport edges the card's center currently sits closest to. */
function toNearestAnchor(card: HTMLDivElement): Anchor {
  const { bottom, left, right, top } = card.getBoundingClientRect();
  return {
    block: (top + bottom) / 2 < window.innerHeight / 2 ? 'top' : 'bottom',
    inline: (left + right) / 2 < window.innerWidth / 2 ? 'left' : 'right',
  };
}

/**
 * A wrapper component that makes its children draggable within the viewport.
 * Supports both click and drag interactions on the drag handle area.
 *
 * The card keeps a fixed distance from the two viewport edges of the corner it currently
 * occupies, so content that grows or shrinks moves the far edge and leaves that corner alone.
 *
 * @param children - The content to render inside the card.
 * @param initialPosition - Which corner, resolved against the text direction, the card is anchored to until it is dragged (default: 'bottom-end').
 * @param className - Additional CSS classes for the container.
 * @returns A draggable floating card component.
 *
 * @example
 * ```tsx
 * <Draggable initialPosition="bottom-end">
 *   {({ isDragging, onPointerDown }) => (
 *     <>
 *       <div onPointerDown={onPointerDown}>Drag Handle</div>
 *       <div>Content</div>
 *     </>
 *   )}
 * </Draggable>
 * ```
 */
export function Draggable({ children, initialPosition = 'bottom-end', className = '' }: DraggableProps) {
  const { i18n } = useTranslation();
  const isRtl = i18n.dir() === 'rtl';
  const [position, setPosition] = useState<Position>({ blockOffset: EDGE_MARGIN, inlineOffset: EDGE_MARGIN });
  // Null while the card sits in its starting corner, so that corner follows the text
  // direction, which is only known once the locale has loaded.
  const [draggedAnchor, setDraggedAnchor] = useState<Anchor | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [returnOffset, setReturnOffset] = useState<Point | null>(null);
  const home = useMemo(() => toAnchor(initialPosition, isRtl), [initialPosition, isRtl]);
  const anchor = draggedAnchor ?? home;
  const dragOffset = useRef<Point>({ x: 0, y: 0 });
  const dragStartPos = useRef<Point>({ x: 0, y: 0 });
  const hasDragged = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const capturedPointer = useRef<{ element: HTMLElement; pointerId: number } | null>(null);

  // Clamps offsets to keep the card within viewport boundaries.
  const clampToViewport = useCallback((newPosition: Position): Position => {
    const card = cardRef.current;
    if (!card) {
      return newPosition;
    }

    return {
      blockOffset: Math.max(0, Math.min(newPosition.blockOffset, window.innerHeight - card.offsetHeight)),
      inlineOffset: Math.max(0, Math.min(newPosition.inlineOffset, window.innerWidth - card.offsetWidth)),
    };
  }, []);

  // Shared handler to clamp position within viewport
  const clampCurrentPosition = useCallback(() => {
    setPosition(currentPosition => {
      const clamped = clampToViewport(currentPosition);
      // Bail out (return the same reference) when clamping doesn't move the card,
      // so no-op resize/observer callbacks don't trigger a re-render.
      if (
        clamped.blockOffset === currentPosition.blockOffset &&
        clamped.inlineOffset === currentPosition.inlineOffset
      ) {
        return currentPosition;
      }
      return clamped;
    });
  }, [clampToViewport]);

  // Keep card within viewport when window is resized (debounced)
  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    function handleResize() {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(clampCurrentPosition, RESIZE_DEBOUNCE_MS);
    }

    window.addEventListener('resize', handleResize);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
    };
  }, [clampCurrentPosition]);

  // Keep card within viewport when content size changes (debounced).
  // Subscribe once on mount — `position` is intentionally not a dependency, since
  // re-subscribing on every position change would recreate the observer (whose
  // initial notification then updates position) in an infinite loop.
  useEffect(() => {
    const card = cardRef.current;
    if (!card) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout>;

    const resizeObserver = new ResizeObserver(() => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(clampCurrentPosition, RESIZE_DEBOUNCE_MS);
    });

    resizeObserver.observe(card);
    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, [clampCurrentPosition]);

  const handlePointerMove = useCallback(
    (event: PointerEvent) => {
      const card = cardRef.current;
      if (!card) {
        return;
      }

      const deltaX = Math.abs(event.clientX - dragStartPos.current.x);
      const deltaY = Math.abs(event.clientY - dragStartPos.current.y);

      if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
        hasDragged.current = true;
        setIsDragging(true);
      }

      const newLeft = event.clientX - dragOffset.current.x;
      const newTop = event.clientY - dragOffset.current.y;

      setPosition(
        clampToViewport({
          blockOffset: anchor.block === 'top' ? newTop : window.innerHeight - newTop - card.offsetHeight,
          inlineOffset: anchor.inline === 'left' ? newLeft : window.innerWidth - newLeft - card.offsetWidth,
        })
      );
    },
    [anchor, clampToViewport]
  );

  const handlePointerEnd = useCallback(() => {
    setIsDragging(false);

    // Re-anchor to the corner it was dropped nearest, so a later resize moves the far
    // edge and leaves that corner alone. Converted from the live rect, so nothing moves.
    const card = cardRef.current;
    if (hasDragged.current && card) {
      const { bottom, left, right, top } = card.getBoundingClientRect();
      const nextAnchor = toNearestAnchor(card);

      setDraggedAnchor(nextAnchor);
      setPosition({
        blockOffset: nextAnchor.block === 'top' ? top : window.innerHeight - bottom,
        inlineOffset: nextAnchor.inline === 'left' ? left : window.innerWidth - right,
      });
    }

    // Release pointer capture (with safety check for test environments)
    if (capturedPointer.current) {
      const { element, pointerId } = capturedPointer.current;
      if (element.hasPointerCapture?.(pointerId)) {
        element.releasePointerCapture(pointerId);
      }
      capturedPointer.current = null;
    }

    document.removeEventListener('pointermove', handlePointerMove);
    document.removeEventListener('pointerup', handlePointerEnd);
    document.removeEventListener('pointercancel', handlePointerEnd);
  }, [handlePointerMove]);

  useEffect(() => {
    return () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerEnd);
      document.removeEventListener('pointercancel', handlePointerEnd);
    };
  }, [handlePointerMove, handlePointerEnd]);

  // Handler to attach to the drag handle element. Returns whether the interaction was a click (not a drag).
  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const card = cardRef.current;
      if (!card) {
        return;
      }

      // Prevent text selection while dragging
      event.preventDefault();

      const { left, top } = card.getBoundingClientRect();

      hasDragged.current = false;
      dragStartPos.current = { x: event.clientX, y: event.clientY };
      dragOffset.current = {
        x: event.clientX - left,
        y: event.clientY - top,
      };

      // Capture pointer to receive events even if pointer leaves the element
      const target = event.target as HTMLElement;
      target.setPointerCapture?.(event.pointerId);
      capturedPointer.current = { element: target, pointerId: event.pointerId };

      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerEnd);
      document.addEventListener('pointercancel', handlePointerEnd);
    },
    [handlePointerMove, handlePointerEnd]
  );

  /**
   * Check if the last interaction was a click (not a drag).
   *
   * @returns True if the last mousedown/mouseup was a click without dragging.
   */
  const wasClick = useCallback((): boolean => {
    return !hasDragged.current;
  }, []);

  /** Sends the card back to the corner it started in, wherever it was dragged to. */
  const resetPosition = useCallback(() => {
    const card = cardRef.current;
    if (!card) {
      return;
    }

    const { height, left, top, width } = card.getBoundingClientRect();

    setDraggedAnchor(null);
    setPosition({ blockOffset: EDGE_MARGIN, inlineOffset: EDGE_MARGIN });

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    // The anchored edges change with the corner, and no transition runs between
    // `top` and `bottom`. Offset the card by the distance it just jumped so it can
    // be animated back to zero instead.
    const homeLeft = home.inline === 'left' ? EDGE_MARGIN : window.innerWidth - EDGE_MARGIN - width;
    const homeTop = home.block === 'top' ? EDGE_MARGIN : window.innerHeight - EDGE_MARGIN - height;
    setReturnOffset({ x: left - homeLeft, y: top - homeTop });
  }, [home]);

  // Drop the offset once the browser has painted with it, so the card animates from
  // where it was rather than starting at zero. Two frames: one to paint, one to move.
  useEffect(() => {
    if (!returnOffset) {
      return;
    }

    let innerFrame: number;
    const outerFrame = requestAnimationFrame(() => {
      innerFrame = requestAnimationFrame(() => setReturnOffset(null));
    });

    return () => {
      cancelAnimationFrame(outerFrame);
      cancelAnimationFrame(innerFrame);
    };
  }, [returnOffset]);

  const contextValue = useMemo(
    () => ({ isDragging, onPointerDown: handlePointerDown, resetPosition, wasClick }),
    [isDragging, handlePointerDown, resetPosition, wasClick]
  );

  return (
    <DraggableContext.Provider value={contextValue}>
      <div
        className={`fixed z-50 ${className}`}
        ref={cardRef}
        style={{
          bottom: anchor.block === 'bottom' ? position.blockOffset : undefined,
          left: anchor.inline === 'left' ? position.inlineOffset : undefined,
          right: anchor.inline === 'right' ? position.inlineOffset : undefined,
          top: anchor.block === 'top' ? position.blockOffset : undefined,
          transform: returnOffset ? `translate(${returnOffset.x}px, ${returnOffset.y}px)` : undefined,
          transition: returnOffset ? undefined : `transform ${RETURN_DURATION_MS}ms ease-out`,
        }}
      >
        {children}
      </div>
    </DraggableContext.Provider>
  );
}

type DraggableContextValue = {
  /** Whether the card is currently being dragged. */
  isDragging: boolean;

  /** Handler to attach to the drag handle element. */
  onPointerDown: (event: React.PointerEvent<HTMLButtonElement>) => void;

  /** Send the card back to the corner it started in. */
  resetPosition: () => void;

  /** Check if the last interaction was a click (not a drag). */
  wasClick: () => boolean;
};

const DraggableContext = createContext<DraggableContextValue | null>(null);

/**
 * Hook to access the draggable card context.
 * Must be used within a Draggable component.
 *
 * @throws Error if used outside of Draggable.
 * @returns The draggable card context with isDragging state and handlers.
 *
 * @example
 * ```tsx
 * function DragHandle() {
 *   const { isDragging, onPointerDown, wasClick } = useDraggable();
 *
 *   const handlePointerUp = () => {
 *     if (wasClick()) {
 *       // Handle click
 *     }
 *   };
 *
 *   return (
 *     <div
 *       onPointerDown={onPointerDown}
 *       onPointerUp={handlePointerUp}
 *       className={isDragging ? 'cursor-grabbing' : 'cursor-pointer'}
 *     >
 *       Drag me
 *     </div>
 *   );
 * }
 * ```
 */
export function useDraggable(): DraggableContextValue {
  const context = useContext(DraggableContext);
  if (!context) {
    throw new Error('useDraggable must be used within a Draggable');
  }
  return context;
}
